import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Close, ChevronLeft, ChevronRight, Download, Trash, Printer } from "./LpIcons";

/**
 * Linchpin-styled file preview overlay (v3). Feature parity intent with v2's
 * `MixedGallery` for the cases Documents actually exercises (image + PDF):
 *
 *   - Fullscreen modal overlay with top toolbar (file name, page indicator,
 *     download / print / delete / close).
 *   - Prev / Next navigation through the file list (keyboard ←/→ and on-screen).
 *   - Thumbnail strip at the bottom; click a thumbnail to jump.
 *   - Image: click-to-zoom (1x ↔ 2x); pan via natural overflow scroll at 2x.
 *   - PDF: rendered in <iframe> (browser-native PDF viewer); print/download via
 *     standard browser actions on the blob URL.
 *   - Unknown types: shows the file name with a Download CTA so nothing is lost.
 *   - Escape closes; outside-click on the dim layer closes.
 *
 * Skipped vs v2's MixedGallery (intentionally, to stay focused for v3):
 *   - Excel/CSV in-app preview — user can Download to view in Excel.
 *   - react-image-gallery's full carousel — we use a simpler prev/next + thumbs
 *     to keep the bundle smaller and the styling Linchpin-native. (The dep is
 *     still in package.json so we could swap in if richer features are needed.)
 *
 * Props:
 *   files:         [{ name, url, mime, capturedAt? }]
 *   open:          boolean — visibility
 *   index:         current file index
 *   onClose:       () => void
 *   onChangeIndex: (next: number) => void   (also fires on keyboard ←/→)
 *   onDelete?:     (index: number) => void  (shows Trash button when provided)
 *   onDownload?:   (file) => void           (defaults to anchor-tag download)
 */
function LpPreview({ files, open, index = 0, onClose, onChangeIndex, onDelete, onDownload }) {
  const [zoom, setZoom] = useState(1); // 1 or 2 — keeps the toggle dead-simple
  const overlayRef = useRef(null);

  const safeIndex = Math.max(0, Math.min(index, (files || []).length - 1));
  const current = files && files[safeIndex];
  const total = (files || []).length;

  // Reset zoom whenever the active file changes — opens at 1x.
  useEffect(() => { setZoom(1); }, [safeIndex, open]);

  // Keyboard nav: ← prev, → next, Esc close. Bound only while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose && onClose(); return; }
      if (e.key === "ArrowLeft" && safeIndex > 0) { e.preventDefault(); onChangeIndex && onChangeIndex(safeIndex - 1); }
      if (e.key === "ArrowRight" && safeIndex < total - 1) { e.preventDefault(); onChangeIndex && onChangeIndex(safeIndex + 1); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, safeIndex, total, onClose, onChangeIndex]);

  // Lock body scroll while the overlay is up — prevents the page underneath
  // from scrolling when the user pans inside the preview.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const goPrev = useCallback(() => { if (safeIndex > 0 && onChangeIndex) onChangeIndex(safeIndex - 1); }, [safeIndex, onChangeIndex]);
  const goNext = useCallback(() => { if (safeIndex < total - 1 && onChangeIndex) onChangeIndex(safeIndex + 1); }, [safeIndex, total, onChangeIndex]);
  const toggleZoom = useCallback(() => setZoom((z) => (z === 1 ? 2 : 1)), []);

  const kind = useMemo(() => {
    if (!current) return "unknown";
    const mime = (current.mime || "").toLowerCase();
    const name = (current.name || "").toLowerCase();
    if (mime.startsWith("image/")) return "image";
    if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
    return "other";
  }, [current]);

  const handleDownload = useCallback(() => {
    if (!current) return;
    if (onDownload) { onDownload(current); return; }
    // Default: trigger a same-tab download via a transient anchor.
    const a = document.createElement("a");
    a.href = current.url;
    a.download = current.name || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [current, onDownload]);

  const handlePrint = useCallback(() => {
    if (!current) return;
    // window.open + print works for images and PDFs across major browsers.
    const w = window.open(current.url, "_blank", "noopener");
    if (w) { try { w.focus(); w.print && w.print(); } catch (e) { /* popup blocked */ } }
  }, [current]);

  const handleDelete = useCallback(() => {
    if (!onDelete) return;
    onDelete(safeIndex);
  }, [onDelete, safeIndex]);

  if (!open || !current) return null;

  // Portal to body — the v3 root is a flex container with overflow:hidden and
  // contains transforms in some children, both of which constrain
  // `position: fixed`. Rendering at the document root lets the overlay cover
  // the full viewport including the FleetLayout header and side nav.
  //
  // IMPORTANT: the outer wrapper has `.lp-v3` and the INNER has `.lp-preview`
  // as separate elements. Our CSS uses descendant selectors like
  // `.lp-v3 .lp-preview`, which require `.lp-v3` to be on an ancestor; putting
  // both classes on the same node leaves the styles unmatched and the overlay
  // renders as an unstyled block at the end of <body> (visible "below the page"
  // after you scroll). Two-element wrap keeps it scoped correctly.
  return ReactDOM.createPortal(
    <div className="lp-v3">
      <div
        className="lp-preview"
        ref={overlayRef}
        onMouseDown={(e) => { if (e.target === overlayRef.current) onClose && onClose(); }}
        role="dialog"
        aria-label={`Preview ${current.name || "file"}`}
      >
      {/* Top toolbar */}
      <div className="lp-preview__toolbar">
        <div className="lp-preview__title" title={current.name}>{current.name || "Untitled"}</div>
        <div className="lp-preview__counter">{safeIndex + 1} / {total}</div>
        <div className="lp-preview__actions">
          <button type="button" className="lp-preview__btn" onClick={handleDownload} aria-label="Download"><Download width={18} height={18} /></button>
          {kind !== "other" && (
            <button type="button" className="lp-preview__btn" onClick={handlePrint} aria-label="Print"><Printer width={18} height={18} /></button>
          )}
          {onDelete && (
            <button type="button" className="lp-preview__btn lp-preview__btn--danger" onClick={handleDelete} aria-label="Delete"><Trash width={18} height={18} /></button>
          )}
          <button type="button" className="lp-preview__btn" onClick={onClose} aria-label="Close"><Close width={18} height={18} /></button>
        </div>
      </div>

      {/* Main viewer */}
      <div className="lp-preview__stage">
        {safeIndex > 0 && (
          <button type="button" className="lp-preview__nav lp-preview__nav--prev" onClick={goPrev} aria-label="Previous"><ChevronLeft width={22} height={22} /></button>
        )}

        {kind === "image" && (
          <div
            className={["lp-preview__imgwrap", zoom === 2 ? "lp-preview__imgwrap--zoomed" : ""].join(" ")}
            onClick={toggleZoom}
            role="button"
            aria-label={zoom === 2 ? "Zoom out" : "Zoom in"}
          >
            <img src={current.url} alt={current.name} style={{ transform: `scale(${zoom})`, transformOrigin: "center" }} />
          </div>
        )}
        {kind === "pdf" && (
          <iframe className="lp-preview__iframe" src={current.url} title={current.name || "PDF"} />
        )}
        {kind === "other" && (
          <div className="lp-preview__other">
            <div className="lp-preview__other-name">{current.name}</div>
            <div className="lp-preview__other-hint">No in-app preview for this file type.</div>
            <button type="button" className="lp-preview__btn lp-preview__btn--solid" onClick={handleDownload}>
              <Download width={16} height={16} /> Download
            </button>
          </div>
        )}

        {safeIndex < total - 1 && (
          <button type="button" className="lp-preview__nav lp-preview__nav--next" onClick={goNext} aria-label="Next"><ChevronRight width={22} height={22} /></button>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div className="lp-preview__thumbs">
          {files.map((f, i) => {
            const tmime = (f.mime || "").toLowerCase();
            const isImg = tmime.startsWith("image/");
            return (
              <button
                key={i}
                type="button"
                className={["lp-preview__thumb", i === safeIndex ? "lp-preview__thumb--active" : ""].join(" ")}
                onClick={() => onChangeIndex && onChangeIndex(i)}
                title={f.name}
              >
                {isImg ? <img src={f.url} alt={f.name} /> : <span className="lp-preview__thumb-ext">{(f.name || "?").split(".").pop().toUpperCase().slice(0, 4)}</span>}
              </button>
            );
          })}
        </div>
      )}
      </div>
    </div>,
    document.body
  );
}

export default React.memo(LpPreview);
